/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { Practise, Role } from '../utils';
import {
  REACT_APP_CLIENT_SECRET,
  REACT_APP_IAM_BASE_URL,
  REACT_APP_CLIENT_ID,
  REACT_APP_REALM_NAME,
  REACT_APP_REDIRECT_URI
} from '../config';
import { ROLES } from '../constants';

const REALM = `realms/${REACT_APP_REALM_NAME}`;

export const USER_INFO = `${REACT_APP_IAM_BASE_URL}/${REALM}/protocol/openid-connect/userinfo`;
export const KC_TOKEN_ENDPOINT = `${REACT_APP_IAM_BASE_URL}/${REALM}/protocol/openid-connect/token`;
export const REALMS_URL = `${REACT_APP_IAM_BASE_URL}/admin/${REALM}`;
export const REALMS_USERS_URL = `${REALMS_URL}/users`;
export const GROUPS_URL = `${REACT_APP_IAM_BASE_URL}/admin/${REALM}/groups`;
export const REALM_ROLES = `${REACT_APP_IAM_BASE_URL}/admin/${REALM}/roles`;

export const exchangeCodeForToken = async (code: string): Promise<string> => {
  const params = new URLSearchParams();
  params.append('grant_type', 'authorization_code');
  params.append('code', code);
  params.append('redirect_uri', REACT_APP_REDIRECT_URI as string);
  params.append('client_id', REACT_APP_CLIENT_ID as string);
  params.append('client_secret', REACT_APP_CLIENT_SECRET as string);

  try {
    const response = await axios.post(KC_TOKEN_ENDPOINT, params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    localStorage.setItem('user_access_token', response.data.access_token);
    return response.data.access_token;
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

// Generate the login URL
export const getKeycloakLoginUrl = (): string => {
  return `${REACT_APP_IAM_BASE_URL}/${REALM}/protocol/openid-connect/auth?client_id=${REACT_APP_CLIENT_ID}&redirect_uri=${encodeURIComponent(
    REACT_APP_REDIRECT_URI as string
  )}&response_type=code&scope=openid`;
};

export const getRealmToken = async () => {
  try {
    const formData = {
      grant_type: 'client_credentials',
      REACT_APP_CLIENT_ID: REACT_APP_CLIENT_ID,
      REACT_APP_CLIENT_SECRET: REACT_APP_CLIENT_SECRET
    };
    const response = await axios.post(KC_TOKEN_ENDPOINT, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    const data = await response.data;
    localStorage.setItem('access_token', data?.access_token);
    return data.access_token;
  } catch (error) {
    console.log('🚀 ~ file: auth.ts:216 ~ getRealmToken ~ error:', error);
    return null;
  }
};

export const getRealmUserInfo = async (id: string, user_access_token: string) => {
  try {
    if (!id || !user_access_token) {
      return;
    }

    const response = await axios.get(`${REALMS_USERS_URL}/${id}`, {
      headers: {
        Authorization: `Bearer ${user_access_token}`
      }
    });
    const data = await response.data;
    return data || {};
  } catch (error) {
    console.log('🚀 ~ file: auth.ts:232 ~ getRealmUserInfo ~ error:', error);
    return null;
  }
};

export const getUserInfo = async (token: string) => {
  try {
    const response = await axios.get(USER_INFO, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const data = await response.data;
    return data || {};
  } catch (error) {
    console.log('🚀 ~ getUserInfo ~ error:', error);
    return null;
  }
};
//to fetch the user information using access token
export const loadUserKeycloakInfo = async (token: string, user_access_token: string) => {
  try {
    const response = await axios.get(USER_INFO, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const userInfo = await response.data;
    const selectedUserRoles = await getRoles(userInfo?.sub, user_access_token);
    const isAdmin = selectedUserRoles?.includes(ROLES.SUPER_ADMIN);
    const isPractiseAdmin = selectedUserRoles?.includes(ROLES.PRACTISE_ADMIN);

    localStorage.setItem('userId', userInfo?.sub);
    localStorage.setItem(
      'userRole',
      isAdmin ? ROLES.SUPER_ADMIN : isPractiseAdmin ? ROLES.PRACTISE_ADMIN : ''
    );
    localStorage.setItem('userName', userInfo?.name);
    localStorage.setItem('userEmail', userInfo?.email);
    localStorage.setItem('organisationId', userInfo['x-hasura-org-id']);

    return userInfo;
  } catch (error: unknown) {
    console.log('🚀 ~ loadUserKeycloakInfo ~ error:', error);
    throw error;
  }
};

export const updateUserRole = async (userId: string, newRole: Role, user_access_token: string) => {
  try {
    if (!userId) {
      console.error(' userId is undefined. Cannot update role.');
      return null;
    }
    // Get existing roles
    const existingRolesResponse = await axios.get(
      `${REALMS_USERS_URL}/${userId}/role-mappings/realm`,
      {
        headers: {
          Authorization: `Bearer ${user_access_token}`
        }
      }
    );

    const existingRoles = existingRolesResponse.data;

    // Filter out only the "Doctor" role
    const doctorRole = existingRoles.find((role: Role) => role.name === ROLES.DOCTOR);

    if (doctorRole) {
      await axios.request({
        method: 'DELETE',
        url: `${REALMS_USERS_URL}/${userId}/role-mappings/realm`,
        headers: {
          Authorization: `Bearer ${user_access_token}`
        },
        data: [doctorRole] // Only remove the Doctor role
      });
    }

    // Assign the new role
    const response = await axios.post(
      `${REALMS_USERS_URL}/${userId}/role-mappings/realm`,
      [newRole], // Pass as array
      {
        headers: {
          Authorization: `Bearer ${user_access_token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('🚀 ~ updateUserRole ~ error:', error);
    return null;
  }
};

export const getAllGroups = async (token: string) => {
  try {
    if (!token) {
      console.error(' token is undefined. Cannot get all groups.');
      return null;
    }
    const response = await axios.get(`${GROUPS_URL}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const data = await response.data;
    return data || {};
  } catch (error) {
    console.log('🚀 ~ getGrpups ~ error:', error);
    return null;
  }
};

export const getAllPractiseList = async (groupId: string, user_access_token: string) => {
  try {
    if (!groupId) {
      console.error(' groupId is undefined. Cannot get practise list.');
      return null;
    }
    const response = await axios.get(`${GROUPS_URL}/${groupId}/children`, {
      headers: {
        Authorization: `Bearer ${user_access_token}`
      }
    });

    const data = await response.data;
    return data || {};
  } catch (error) {
    console.log('🚀 ~ getAllPractiseList ~ error:', error);
    return null;
  }
};

export const getGroupbyId = async (groupId: string, user_access_token: string) => {
  try {
    if (!groupId) {
      console.error(' groupId is undefined. Cannot get group by id.');
      return null;
    }
    const response = await axios.get(`${GROUPS_URL}/${groupId}`, {
      headers: { Authorization: `Bearer ${user_access_token}` }
    });
    return response.data || null;
  } catch (error: any) {
    console.error('🚀 getGroupbyId error:', error.response?.status, error.response?.data);
    return null;
  }
};

export const getRealmRoles = async (user_access_token: string): Promise<Role[] | null> => {
  try {
    const response = await axios.get(`${REALM_ROLES}`, {
      headers: { Authorization: `Bearer ${user_access_token}` }
    });
    return response.data || [];
  } catch (error: any) {
    console.error('🚀 getRealmRoles error:', error.response?.status, error.response?.data);
    return null;
  }
};

export const assignRoleToUser = async (userId: string, body: Role[], user_access_token: string) => {
  if (!userId) {
    console.error(' userId is undefined. Cannot assign roles.');
    return null;
  }

  try {
    const response = await axios.post(`${REALMS_USERS_URL}/${userId}/role-mappings/realm`, body, {
      headers: { Authorization: `Bearer ${user_access_token}` }
    });
    return response.data || null;
  } catch (error: any) {
    console.error('🚀 assignRoleToUser error:', error.response?.status, error.response?.data);
    return null;
  }
};

export const getRoles = async (
  userId: string,
  user_access_token: string
): Promise<string[] | null> => {
  try {
    if (!userId) {
      console.error(' userId is undefined. Cannot getRoles role.');
      return null;
    }
    const response = await axios.get(`${REALMS_USERS_URL}/${userId}/role-mappings`, {
      headers: { Authorization: `Bearer ${user_access_token}` }
    });
    const roles = response.data?.realmMappings?.map((i: { name: string }) => i.name) || [];
    return roles;
  } catch (error: any) {
    console.error('🚀 getRoles error:', error.response?.status, error.response?.data);
    return null;
  }
};

export const addOrgnisationOrPractise = async (
  groupId: string,
  body: Practise,
  user_access_token: string
) => {
  try {
    if (!groupId) {
      console.error(' groupId is undefined. Cannot add organisation to group.');
      return null;
    }
    const response = await axios.post(`${REALMS_URL}/groups/${groupId}/children`, body, {
      headers: { Authorization: `Bearer ${user_access_token}` }
    });
    return response.data || null;
  } catch (error: any) {
    console.error(
      '🚀 addOrgnisationOrPractise error:',
      error.response?.status,
      error.response?.data
    );
    return null;
  }
};

export const addUserToGroup = async (
  userId: string,
  groupId: string,
  user_access_token: string
) => {
  try {
    if (!groupId) {
      console.error(' groupId is undefined. Cannot add user to group.');
      return null;
    }
    if (!userId) {
      console.error(' userId is undefined. Cannot add user to group.');
      return null;
    }
    const response = await axios.put(
      `${REALMS_USERS_URL}/${userId}/groups/${groupId}`,
      {},
      { headers: { Authorization: `Bearer ${user_access_token}` } }
    );
    return response.data || null;
  } catch (error: any) {
    console.error('🚀 addUserToGroup error:', error.response?.status, error.response?.data);
    return null;
  }
};

export const updateDepartmentNameKeycloak = async (
  groupId: string,
  name: string,
  user_access_token: string
) => {
  try {
    if (!groupId) {
      console.error(' groupId is undefined. Cannot update department name.');
      return null;
    }
    const response = await axios.put(
      `${REALMS_URL}/groups/${groupId}`,
      { name },
      { headers: { Authorization: `Bearer ${user_access_token}` } }
    );
    return response.data || null;
  } catch (error: any) {
    console.error(
      '🚀 updateDepartmentNameKeycloak error:',
      error.response?.status,
      error.response?.data
    );
    return null;
  }
};
